import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { SimpleSelect } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Mail, Package, ShoppingBag, LogOut, Eye, CreditCard, Trash2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PaymentModal } from '../../components/shop/PaymentModal';
import { supabase } from '../../lib/supabase';
import { 
  sendCustomerOTP, 
  verifyCustomerOTP, 
  generateAccessToken, 
  getCustomerProfileByToken 
} from '../../services/customerProfileService';
import { useTenantContext } from '../../context/TenantContext';
import { getDomainFilterParams, applyDomainFilterToQuery } from '../../hooks/useDomainQuery';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const MyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'ShoppingBag', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [activeTab, setActiveTab] = useState<string>('email');
  const [email, setEmail] = useState<string>('');
  const [orderCode, setOrderCode] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  // Interface pour les commandes
  interface Order {
    id: number;
    order_number: string;
    created_at: string;
    status: string;
    total_amount: number;
  }
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerProfile, setCustomerProfile] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const ordersPerPage = 10;
  
  // Vérifier si l'utilisateur est déjà authentifié
  useEffect(() => {
    const checkAuthentication = async () => {
      // Vérifier si on vient de OrderDetailsPage avec state preserveAuth
      const location = window.location;
      const state = location.state as any;
      const preserveAuth = state?.preserveAuth;
      
      // Récupérer le paramètre de redirection depuis l'URL
      const searchParams = new URLSearchParams(window.location.search);
      const redirectPath = searchParams.get('redirect');
      
      const token = localStorage.getItem('customer_access_token');
      if (token) {
        try {
          const profile = await getCustomerProfileByToken(token);
          if (profile) {
            setIsAuthenticated(true);
            setCustomerProfile(profile);
            setCustomerName(profile.full_name || profile.email.split('@')[0]);
            // SSO: si le token e-learning est absent, le définir avec le token shop
            if (!localStorage.getItem('elearning_student_token')) {
              localStorage.setItem('elearning_student_token', token);
            }
            if (profile.id) {
              fetchOrders(profile.id);
            }
            
            // Si connecté et qu'il y a une redirection, rediriger
            if (redirectPath) {
              navigate(redirectPath);
              return;
            }
          } else if (!preserveAuth) {
            // Token invalide ou expiré (ne pas supprimer si preserveAuth est true)
            localStorage.removeItem('customer_access_token');
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          if (!preserveAuth) {
            localStorage.removeItem('customer_access_token');
          }
        }
      }
    };
    
    checkAuthentication();
  }, [navigate]);
  
  // Recharger les commandes quand la page change
  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('customer_access_token');
      if (token) {
        getCustomerProfileByToken(token).then(profile => {
          if (profile && profile.id) {
            fetchOrders(profile.id);
          }
        });
      }
    }
  }, [currentPage, searchQuery, statusFilter]);
  
  // Envoyer un code OTP par email
  const handleSendOTP = async () => {
    if (!email) {
      toast({
        title: 'Email requis',
        description: 'Veuillez entrer votre adresse email',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await sendCustomerOTP(email);
      if (result) {
        toast({
          title: 'Code envoyé',
          description: 'Un code de vérification a été envoyé à votre adresse email',
          variant: 'default'
        });
        setIsVerifying(true);
      } else {
        throw new Error('Impossible d\'envoyer le code OTP');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du code OTP:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer le code de vérification',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Vérifier le code OTP
  const handleVerifyOTP = async () => {
    if (!otp) {
      toast({
        title: 'Code requis',
        description: 'Veuillez entrer le code de vérification',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const isValid = await verifyCustomerOTP(email, otp);
      if (isValid) {
        // Générer un token d'accès
        const token = await generateAccessToken(email);
        if (token) {
          localStorage.setItem('customer_access_token', token);
          // SSO: synchroniser le token côté e-learning
          localStorage.setItem('elearning_student_token', token);
          
          // Récupérer le profil client
          const profile = await getCustomerProfileByToken(token);
          if (profile) {
            setIsAuthenticated(true);
            setCustomerName(profile.full_name || profile.email.split('@')[0]);
            if (profile.id) {
              fetchOrders(profile.id);
            }
            
            toast({
              title: 'Connexion réussie',
              description: 'Vous êtes maintenant connecté à votre compte',
              variant: 'default'
            });
            
            // Vérifier s'il y a une redirection après connexion
            const searchParams = new URLSearchParams(window.location.search);
            const redirectPath = searchParams.get('redirect');
            if (redirectPath) {
              navigate(redirectPath);
              return;
            }
          }
        } else {
          throw new Error('Impossible de générer un token d\'accès');
        }
      } else {
        toast({
          title: 'Code invalide',
          description: 'Le code de vérification est invalide ou expiré',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du code OTP:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de vérifier le code',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setIsVerifying(false);
      setOtp('');
    }
  };
  
  // Rechercher une commande par code
  const handleSearchOrder = async () => {
    if (!orderCode) {
      toast({
        title: 'Code requis',
        description: 'Veuillez entrer le code de votre commande',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      let query = supabase
        .from('shop_orders')
        .select('*')
        .eq('order_number', orderCode);
      
      // Appliquer le filtrage par domaine
      query = applyDomainFilterToQuery(query);
      
      const { data, error } = await query.single();
      
      if (error) throw error;
      
      if (data) {
        navigate(`/boutique/commandes/${data.id}`);
      } else {
        toast({
          title: 'Commande introuvable',
          description: 'Aucune commande trouvée avec ce code',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de trouver la commande',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Récupérer les commandes d'un client
  // Fonction pour payer une commande en attente
  const handlePayOrder = async (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la navigation vers la page de détail
    
    try {
      setIsLoading(true);
      
      // Récupérer les détails de la commande
      const { data: orderData, error: orderError } = await supabase
        .from('shop_orders')
        .select('*')
        .eq('id', orderId)
        .single();
      
      if (orderError) throw orderError;
      
      // Ouvrir la modale de paiement avec les détails de la commande
      setSelectedOrder({
        id: orderData.id,
        order_number: orderData.order_number,
        total_amount: orderData.total_amount,
        customer_name: customerName,
        customer_email: customerEmail
      });
      setIsPaymentModalOpen(true);
      
    } catch (error) {
      console.error('Erreur lors de la préparation du paiement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de procéder au paiement. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction appelée après un paiement réussi
  const handlePaymentSuccess = () => {
    // Fermer la modale
    setIsPaymentModalOpen(false);
    
    // Rafraîchir la liste des commandes
    if (customerProfile) {
      fetchOrders(customerProfile.id);
    }
    
    // Afficher un message de succès
    toast({
      title: 'Paiement réussi',
      description: 'Votre commande a été payée avec succès.',
      variant: 'default'
    });
  };
  
  // Fonction pour supprimer une commande en attente
  const handleDeleteOrder = async (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la navigation vers la page de détail
    
    const confirmDelete = window.confirm('Voulez-vous vraiment supprimer cette commande ? Cette action est irréversible.');
    if (!confirmDelete) return;
    
    try {
      setIsLoading(true);
      console.log('Tentative de suppression de la commande:', orderId);
      
      // Vérifier si la commande existe
      const { data: orderCheck, error: checkError } = await supabase
        .from('shop_orders')
        .select('id, status')
        .eq('id', orderId)
        .single();
      
      if (checkError) {
        console.error('Erreur lors de la vérification de la commande:', checkError);
        throw checkError;
      }
      
      console.log('Commande trouvée:', orderCheck);
      
      // Supprimer la commande
      const { error } = await supabase
        .from('shop_orders')
        .delete()
        .eq('id', orderId);
      
      if (error) {
        console.error('Erreur détaillée lors de la suppression:', error);
        throw error;
      }
      
      console.log('Commande supprimée avec succès');
      
      // Mettre à jour la liste des commandes localement sans attendre fetchOrders
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      
      toast({
        title: 'Commande supprimée',
        description: 'La commande a été supprimée avec succès.',
        variant: 'default'
      });
      
      // Rafraîchir la liste des commandes depuis la base de données
      if (customerProfile) {
        fetchOrders(customerProfile.id);
      }
      
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la commande. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async (customerId: number) => {
    try {
      // Récupérer le nombre total de commandes pour la pagination
      let countQuery = supabase
        .from('shop_orders')
        .select('id', { count: 'exact' })
        .eq('customer_profile_id', customerId);
      
      // Appliquer le filtrage par domaine
      countQuery = applyDomainFilterToQuery(countQuery);
      
      if (searchQuery) {
        countQuery = countQuery.ilike('order_number', `%${searchQuery}%`);
      }
      if (statusFilter) {
        countQuery = countQuery.eq('status', statusFilter);
      }
      const countResponse = await countQuery;
      
      if (countResponse.error) throw countResponse.error;
      setTotalOrders(countResponse.count || 0);
      
      // Récupérer les commandes pour la page actuelle
      const from = (currentPage - 1) * ordersPerPage;
      const to = from + ordersPerPage - 1;
      
      let dataQuery = supabase
        .from('shop_orders')
        .select('*')
        .eq('customer_profile_id', customerId);
      
      // Appliquer le filtrage par domaine
      dataQuery = applyDomainFilterToQuery(dataQuery);
      
      if (searchQuery) {
        dataQuery = dataQuery.ilike('order_number', `%${searchQuery}%`);
      }
      if (statusFilter) {
        dataQuery = dataQuery.eq('status', statusFilter);
      }
      const { data, error } = await dataQuery
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (error) throw error;
      
      if (data) {
        setOrders(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer vos commandes',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `Mes commandes - ${tenant?.name || 'Boutique'}`,
      description: `Consultez l'historique de vos commandes et suivez leur statut sur ${tenant?.name || 'notre boutique'}.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/boutique/commandes`
    };
  }, [tenant]);

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={seoConfig.title} 
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />

      <div className="text-white relative overflow-hidden pt-20">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.ShoppingBag;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>
        <div className="relative z-10 container mx-auto px-4 py-12 max-w-3xl">
          <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-2">Mes commandes</h1>
              <p className="text-white/70">Suivez vos commandes et accédez à votre historique d'achats</p>
            </div>
          {isAuthenticated ? (
            <Card className="bg-black/20 border-white/10 text-white backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Bonjour, {customerName}</CardTitle>
                <CardDescription className="text-white/70">
                  Voici vos commandes récentes
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                {/* Barre de recherche et filtre */}
                <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0 mb-4">
                  <Input
                    placeholder="Rechercher par numéro de commande"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="bg-white/5 border-white/10 flex-1"
                  />
                  <div className="w-full md:w-56">
                    <SimpleSelect
                      value={statusFilter}
                      onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}
                      options={[
                        { value: '', label: 'Tous les statuts' },
                        { value: 'paid', label: 'Payée' },
                        { value: 'pending_payment', label: 'En attente de paiement' },
                        { value: 'processing', label: 'En traitement' },
                        { value: 'shipped', label: 'Expédiée' },
                        { value: 'completed', label: 'Terminée' }
                      ]}
                    />
                  </div>
                </div>
                {orders.length > 0 ? (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.id} 
                        className="bg-white/5 p-4 rounded-lg hover:bg-white/10 transition-colors"
                        onClick={() => navigate(`/boutique/commandes/${order.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-semibold">Commande #{order.order_number}</h3>
                            <p className="text-sm text-white/70">
                              {new Date(order.created_at).toLocaleDateString('fr-FR')} à {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              order.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                              order.status === 'processing' ? 'bg-blue-500/20 text-blue-300' :
                              order.status === 'shipped' ? 'bg-purple-500/20 text-purple-300' :
                              order.status === 'paid' ? 'bg-green-500/20 text-green-300' :
                              'bg-yellow-500/20 text-yellow-300'
                            }`}>
                              {order.status === 'completed' ? 'Terminée' :
                               order.status === 'processing' ? 'En traitement' :
                               order.status === 'shipped' ? 'Expédiée' :
                               order.status === 'paid' ? 'Payée' : 'En attente'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="border-t border-white/10 pt-2 mt-2 flex justify-between items-center">
                          <p className="font-semibold">
                            {new Intl.NumberFormat('fr-FR').format(order.total_amount)} FCFA
                          </p>
                          
                          {/* Boutons d'action */}
                          <div className="flex space-x-2">
                            {/* Afficher le bouton Payer uniquement pour les commandes qui ne sont pas payées */}
                            {(order.status !== 'paid' && order.status !== 'processing' && 
                              order.status !== 'shipped' && order.status !== 'completed') && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="transition-all duration-200"
                                style={{
                                  backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33`,
                                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                                  color: tenant?.config?.color1 || '#fe8d17'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                                  e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                                  e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                                }}
                                onClick={(e) => handlePayOrder(order.id, e)}
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                Payer
                              </Button>
                            )}
                            
                            {/* Afficher le bouton Supprimer uniquement pour les commandes qui ne sont pas payées */}
                            {(order.status !== 'paid' && order.status !== 'processing' && 
                              order.status !== 'shipped' && order.status !== 'completed') && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="transition-all duration-200"
                                style={{
                                  backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33`,
                                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                                  color: tenant?.config?.color1 || '#fe8d17'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                                  e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                                  e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                                }}
                                onClick={(e) => handleDeleteOrder(order.id, e)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Supprimer
                              </Button>
                            )}
                            
                            {/* Bouton Voir pour toutes les commandes */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="bg-white/10 hover:bg-white/20 border-white/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/boutique/commandes/${order.id}`);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Contrôles de pagination */}
                    {totalOrders > ordersPerPage && (
                      <div className="flex justify-center items-center space-x-2 mt-6">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="transition-all duration-200"
                          style={{
                            borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                            e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                          }}
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Précédent
                        </Button>
                        
                        <div className="text-sm"
                          style={{
                            color: tenant?.config?.color4 || '#9ca3af'
                          }}
                        >
                          Page {currentPage} sur {Math.ceil(totalOrders / ordersPerPage)}
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="transition-all duration-200"
                          style={{
                            borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                            e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                          }}
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalOrders / ordersPerPage)))}
                          disabled={currentPage >= Math.ceil(totalOrders / ordersPerPage)}
                        >
                          Suivant
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShoppingBag className="h-12 w-12 mx-auto text-white/30 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Aucune commande trouvée</h3>
                    <p className="text-white/70 mb-4">
                      Vous n'avez pas encore effectué de commande
                    </p>
                    <Button 
                      onClick={() => navigate('/boutique')}
                      className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                      style={{
                        backgroundColor: tenant?.config?.color1 || '#fe8d17',
                        color: tenant?.config?.color3 || '#ffffff',
                        backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                        borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                        boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                      }}
                    >
                      Visiter la boutique
                    </Button>
                  </div>
                )}
              </CardContent>
              
              <CardFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    localStorage.removeItem('customer_access_token');
                    // SSO: Nettoyer également le token e-learning
                    localStorage.removeItem('elearning_student_token');
                    setIsAuthenticated(false);
                    setOrders([]);
                  }}
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundColor: `${tenant?.config?.color22 || '#bf0101'}99`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = tenant?.config?.color22 || '#bf0101';
                    e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${tenant?.config?.color22 || '#bf0101'}99`;
                    e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Déconnexion
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <Card className="bg-black/20 border-white/10 text-white backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Accéder à mes commandes</CardTitle>
                <CardDescription className="text-white/70">
                  Consultez vos commandes sans avoir besoin de créer un compte
                </CardDescription>
              </CardHeader>
            
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="email">Par email</TabsTrigger>
                    <TabsTrigger value="code">Par code de commande</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="email">
                    {!isVerifying ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Adresse email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="votre@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-white/5 border-white/10"
                          />
                          <p className="text-sm text-white/70">
                            Entrez l'adresse email utilisée lors de votre commande
                          </p>
                        </div>
                        
                        <Button 
                          onClick={handleSendOTP}
                          disabled={isLoading}
                          className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                          style={{
                            backgroundColor: tenant?.config?.color1 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff',
                            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                            borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                            boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                          }}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Mail className="mr-2 h-4 w-4" />
                              Recevoir un code de vérification
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="otp">Code de vérification</Label>
                          <Input
                            id="otp"
                            type="text"
                            placeholder="123456"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            className="bg-white/5 border-white/10"
                          />
                          <p className="text-sm text-white/70">
                            Entrez le code de vérification envoyé à {email}
                          </p>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            onClick={() => setIsVerifying(false)}
                            className="flex-1"
                          >
                            Retour
                          </Button>
                          <Button 
                            onClick={handleVerifyOTP}
                            disabled={isLoading}
                            className="flex-1 shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                            style={{
                              backgroundColor: tenant?.config?.color1 || '#fe8d17',
                              color: tenant?.config?.color3 || '#ffffff',
                              backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                              borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                              boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                            }}
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Vérification...
                              </>
                            ) : (
                              'Vérifier le code'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="code">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="orderCode">Code de commande</Label>
                        <Input
                          id="orderCode"
                          type="text"
                          placeholder="ABC123"
                          value={orderCode}
                          onChange={(e) => setOrderCode(e.target.value)}
                          className="bg-white/5 border-white/10"
                        />
                        <p className="text-sm text-white/70">
                          Entrez le code de commande reçu par email
                        </p>
                      </div>
                      
                      <Button 
                        onClick={handleSearchOrder}
                        disabled={isLoading}
                        className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Recherche en cours...
                          </>
                        ) : (
                          <>
                            <Package className="mr-2 h-4 w-4" />
                            Rechercher ma commande
                          </>
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
          </motion.div>
        </div>
      
      {/* Modale de paiement */}
      {selectedOrder && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          order={selectedOrder}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
    </PublicLayout>
  );
};

export default MyOrdersPage;
